import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { finishedSessions, outboxCounts, getLastGymId } from '../../db/repo';
import { acceptAdjustment } from '../session/createSession';
import { adjustmentWeightCrossesGyms, gymLabel } from '../../domain/gym';
import { syncNow } from '../../sync/syncClient';
import { isSyncConfigured } from '../../sync/config';
import { Toast } from '../../ui/primitives';
import { IconSync } from '../../ui/icons';
import { formatDate } from '../../lib/dates';
import { formatWeight } from '../../domain/weight';
import { EXERCISE_BY_ID } from '../../seed/exercises';
import type { GymId, SuggestedAdjustment } from '../../domain/types';

export function CoachScreen() {
  const [toast, setToast] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  // Keys of adjustments the user has tapped Apply on this view, so the button gives
  // feedback (it otherwise looks like nothing happened). The override itself lives
  // in meta and is one-shot: applied to that exercise's next session, then cleared.
  const [applied, setApplied] = useState<Set<string>>(new Set());
  const coached = useLiveQuery(async () => (await finishedSessions()).filter((s) => s.coaching || (s.suggestedAdjustments?.length ?? 0) > 0), []);
  const counts = useLiveQuery(() => outboxCounts(), []);
  // The gym the next session will be at, used only to warn when an accepted weight
  // won't travel there. The actual decision is re-made at plan time.
  const nextGymId = useLiveQuery(() => getLastGymId(), []);
  const configured = isSyncConfigured();

  async function doSync() {
    setSyncing(true);
    const { flush, measurements, pull } = await syncNow();
    setSyncing(false);
    if (!flush.configured) { setToast('Notion not configured. See More > Settings.'); return; }
    const meas = measurements.synced ? `, measurements ${measurements.synced}` : '';
    setToast(`Synced ${flush.synced}${meas}, coaching pulled ${pull.applied}${flush.failed ? `, ${flush.failed} failed` : ''}`);
  }

  async function accept(adj: SuggestedAdjustment, key: string, sourceGymId: GymId | null) {
    const id = await acceptAdjustment(adj, sourceGymId);
    if (id) setApplied((prev) => new Set(prev).add(key));
    if (!id) { setToast('Could not match exercise.'); return; }
    // If the advice carries a weight from a gym he isn't next training at, say so
    // rather than letting a silently-dropped number look like it applied.
    const crossGym = adjustmentWeightCrossesGyms({ ...adj, sourceGymId }, nextGymId ?? null);
    setToast(crossGym
      ? `Applied. The weight was measured at ${gymLabel(sourceGymId)}, so at ${gymLabel(nextGymId ?? null)} the intent carries over but the load comes from your history there.`
      : 'Applied — takes effect on your next session for this exercise.');
  }

  function adjWeight(adj: SuggestedAdjustment): string | null {
    if (adj.suggestedWeightKg == null) return null;
    const ex = adj.exerciseId ? EXERCISE_BY_ID.get(adj.exerciseId) : undefined;
    return ex ? formatWeight(adj.suggestedWeightKg, ex) : `${adj.suggestedWeightKg} kg`;
  }

  function swapTargetName(adj: SuggestedAdjustment): string {
    const byId = adj.swapToExerciseId ? EXERCISE_BY_ID.get(adj.swapToExerciseId)?.name : undefined;
    return byId ?? adj.swapToExerciseName ?? 'another exercise';
  }

  // What the chip says the override will do when applied.
  function adjLabel(adj: SuggestedAdjustment): string {
    const w = adjWeight(adj);
    switch (adj.kind) {
      case 'swap': return `swap to ${swapTargetName(adj)}`;
      case 'hold': return w ? `hold at ${w}` : 'hold load';
      case 'deload': return w ? `deload to ${w}` : 'deload -10%';
      case 'load': return w ? `set ${w}` : 'load';
      default:
        if (adj.suggestedGrip) return `grip: ${adj.suggestedGrip}`;
        return w ?? adj.kind;
    }
  }

  // An override the app can actually apply (vs. pure commentary).
  function isActionable(adj: SuggestedAdjustment): boolean {
    if (adj.kind === 'swap') return Boolean(adj.swapToExerciseId || adj.swapToExerciseName);
    if (adj.kind === 'hold' || adj.kind === 'deload' || adj.kind === 'load') return true;
    return adj.suggestedWeightKg != null || adj.suggestedRepRange != null || Boolean(adj.suggestedGrip);
  }

  return (
    <div className="screen">
      <div className="spread">
        <div className="screen-title">Coach</div>
        <button className="btn sm" onClick={doSync} disabled={syncing}>
          <IconSync width={18} height={18} /> {syncing ? 'Syncing' : 'Sync'}
        </button>
      </div>

      {!configured && (
        <div className="info-banner">
          Coaching is written by the Cowork "gym-coach" skill into Notion, then pulled here. Set up the Worker + Notion in{' '}
          <Link to="/more/settings" style={{ color: 'var(--accent)' }}>Settings</Link> to enable it. The app works fully offline without it.
        </div>
      )}
      {configured && counts && (counts.pending > 0 || counts.error > 0) && (
        <div className="info-banner">{counts.pending} session(s) waiting to sync{counts.error ? `, ${counts.error} errored` : ''}.</div>
      )}

      {coached && coached.length === 0 && (
        <div className="empty">No coaching yet. Finish a session, sync, then run the gym-coach skill on your laptop.</div>
      )}

      {coached?.map((s) => (
        <div key={s.id} className="card">
          <div className="spread">
            <strong>{s.routineLabel}</strong>
            <span className="faint" style={{ fontSize: 12 }}>{formatDate(s.date)} - {gymLabel(s.gymId)}</span>
          </div>
          {s.coaching && <div style={{ marginTop: 10, whiteSpace: 'pre-wrap', fontSize: 15 }}>{s.coaching}</div>}
          {s.suggestedAdjustments && s.suggestedAdjustments.length > 0 && (
            <>
              <div className="section-label" style={{ marginTop: 12 }}>Suggested adjustments</div>
              {s.suggestedAdjustments.map((adj, i) => {
                const key = `${s.id}:${i}`;
                const isApplied = applied.has(key);
                // Which gym the advice's weight belongs to. A weekly review sits on
                // one session but covers both gyms, so the coach can stamp each chip
                // with its own sourceGymId; otherwise it's the session's gym.
                // nextGymId is where he trains next: a weight from the other gym's
                // machine won't be applied — the hold/deload intent still will be.
                const srcGym: GymId | null = adj.sourceGymId ?? s.gymId;
                const crossGym = adjustmentWeightCrossesGyms(
                  { ...adj, sourceGymId: srcGym }, nextGymId ?? null,
                );
                return (
                  <div key={i} className="card tight" style={{ marginBottom: 8 }}>
                    <div className="spread">
                      <div className="grow">
                        <strong>{adj.exerciseName ?? adj.exerciseId ?? 'General'}</strong>
                        <div className="faint" style={{ fontSize: 13, marginTop: 2 }}>{adj.detail}</div>
                      </div>
                      {isActionable(adj) && (
                        <button className={`btn sm ${isApplied ? 'ghost' : 'primary'}`} onClick={() => accept(adj, key, srcGym)} disabled={isApplied}>
                          {isApplied ? 'Applied ✓' : 'Apply'}
                        </button>
                      )}
                    </div>
                    <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
                      <span className="chip">{adjLabel(adj)}</span>
                      {crossGym && <span className="chip warn">weight is {gymLabel(srcGym)}</span>}
                    </div>
                    {crossGym && (
                      <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                        You're set to train at {gymLabel(nextGymId ?? null)} next. The call still applies;
                        the load comes from your {gymLabel(nextGymId ?? null)} history instead.
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      ))}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
